import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';

const CONTAINER = 'max-w-6xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-600 leading-relaxed';

/**
 * Feb 2026 marketing redesign — restored the original personal, authentic
 * About narrative (founder photo + story + mission + vision) that the user
 * asked for back after the iter87 draft felt too generic. Design is
 * refreshed with the new brand rhythm (navy hero, light-grey story card,
 * navy vision panel) but the copy is verbatim from the original page.
 */

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Meet the founder — navy hero */}
      <section className="bg-[#0B132B] text-white pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className={`${CONTAINER} grid md:grid-cols-5 gap-10 sm:gap-14 items-center`}>
          <div className="md:col-span-2">
            <div
              className="aspect-square w-full max-w-xs mx-auto md:mx-0 rounded-3xl overflow-hidden shadow-2xl shadow-red-900/30 ring-4 ring-white/10"
              data-testid="founder-photo"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c81cb521-10eb-499a-aced-0c520ab93005/artifacts/gc6dgzwb_image0.jpeg"
                alt="Alia Veerasawmy, Founder & CEO of ShiftCover365"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C] mb-3">Meet the founder</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight" data-testid="founder-name">
              Alia Veerasawmy
            </h1>
            <p className="text-sm text-white/60 mt-2 mb-6">Founder &amp; CEO, ShiftCover365 Ltd</p>
            <div className="space-y-4 text-[15px] text-white/85 leading-relaxed max-w-xl">
              <p>
                I started ShiftCover365 after a chance conversation inside a small Post Office. The Postmaster was working the counter alone — no cover, no break, no easy way to find a trusted hand at short notice.
              </p>
              <p>
                That moment stuck with me. I knew there had to be a smarter way for Postmasters to find reliable cover, protect their tills, and reclaim their time.
              </p>
              <p className="text-white font-semibold">
                ShiftCover365 exists to give branch owners back their freedom — with the accountability and trust they deserve.
              </p>
            </div>
            <a
              href="mailto:support@shiftcover365.co.uk"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] px-5 h-11 text-sm font-semibold hover:bg-white/90 transition-colors"
              data-testid="founder-email-link"
            >
              <Mail className="h-4 w-4" /> Email the founder
            </a>
          </div>
        </div>
      </section>

      {/* About + Mission (white) */}
      <section className="bg-white py-24 lg:py-32">
        <div className={`${CONTAINER} grid lg:grid-cols-2 gap-10 lg:gap-14 items-start`} data-testid="about-block-about">
          <div>
            <p className={EYEBROW}>About</p>
            <h2 className={`${H2} mt-4`}>About ShiftCover365</h2>
            <p className={`${BODY} mt-6`}>
              ShiftCover365 was created to solve one of the most overlooked challenges faced by Post Office branches — finding reliable, trusted staff cover at short notice while keeping tills and operations secure.
            </p>
            <p className={`${BODY} mt-4`}>
              We&rsquo;re redefining how branch owners manage absences, accountability, and trust.
            </p>
          </div>
          <div className="rounded-3xl bg-[#0B132B] text-white p-8 sm:p-10 shadow-xl relative overflow-hidden" data-testid="about-mission-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#DA291C]/10 rounded-full -mr-12 -mt-12" aria-hidden />
            <p className="uppercase tracking-[0.2em] text-[11px] font-bold text-[#DA291C] mb-3">Our Mission</p>
            <p className="text-lg sm:text-xl font-medium leading-relaxed">
              Our mission is simple: to help Postmasters maintain business continuity, protect their revenue, and ensure peace of mind — every single day of the year.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story (light grey) */}
      <section className="bg-[#F3F4F6] py-24 lg:py-32" data-testid="about-block-story">
        <div className={CONTAINER}>
          <div className="rounded-3xl bg-white p-8 sm:p-12 lg:p-16 shadow-[0_16px_40px_rgba(11,19,43,0.06)]">
            <p className={EYEBROW}>Our story</p>
            <h2 className={`${H2} mt-3`}>How ShiftCover365 began</h2>
            <div className="mt-6 space-y-5 text-base text-slate-700 leading-relaxed max-w-3xl">
              <p>The idea for ShiftCover365 started on an ordinary day inside a small Post Office.</p>
              <p>I walked in to post a parcel and noticed there wasn&rsquo;t anyone there except the Postmaster, working alone behind the counter.</p>
              <p>We started talking, and they shared the challenges they face every day — finding reliable cover when staff call in sick, struggling to take time off, and feeling tied to the branch around the clock.</p>
              <p className="font-semibold text-[#0B132B]">That moment sparked something.</p>
              <p>I realised there needed to be a smarter way for Postmasters to find trusted cover, protect their tills, and run their businesses with peace of mind.</p>
            </div>

            <blockquote className="mt-10 rounded-2xl bg-[#F3F4F6] border-l-4 border-[#DA291C] p-6 sm:p-8" data-testid="about-story-quote">
              <svg className="h-6 w-6 text-[#DA291C] mb-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
              </svg>
              <p className="text-lg sm:text-xl font-medium text-[#0B132B] leading-relaxed italic">
                If I were a business owner, I&rsquo;d want to know I have the freedom to step away when I need to — without worrying. I&rsquo;d expect that freedom, and the financial stability that comes with it.
              </p>
            </blockquote>

            <p className="mt-8 text-base text-slate-700 leading-relaxed max-w-3xl">
              That&rsquo;s how ShiftCover365 was born — from one conversation, one observation, and one mission:{' '}
              <span className="font-semibold text-[#0B132B]">to give Postmasters back their time, their trust, and their freedom.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Our Vision (white with red panel) */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-block-vision">
        <div className={`${CONTAINER} grid lg:grid-cols-2 gap-10 lg:gap-14 items-start`}>
          <div>
            <p className={EYEBROW}>Our vision</p>
            <h2 className={`${H2} mt-4`}>Trust, 365 days a year.</h2>
            <p className={`${BODY} mt-6`}>
              ShiftCover365&rsquo;s vision is to become the most trusted shift-management and accountability platform across the UK&rsquo;s retail and postal network.
            </p>
            <p className={`${BODY} mt-4`}>
              We aim to give Postmasters — and eventually other local business owners — the confidence to step away from their business knowing that every shift, every till, and every transaction is protected.
            </p>
            <p className={`${BODY} mt-4`}>
              By blending smart technology with human accountability, ShiftCover365 helps businesses run smoother, safer, and smarter — <span className="font-semibold text-[#0B132B]">365 days a year</span>.
            </p>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-[#DA291C] to-[#a8190f] text-white p-8 sm:p-10 shadow-xl shadow-red-900/20 relative overflow-hidden" data-testid="about-vision-quote">
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/5 rounded-full" aria-hidden />
            <svg className="h-7 w-7 text-white/70 mb-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
            </svg>
            <p className="text-xl sm:text-2xl font-semibold leading-snug">
              At ShiftCover365, we&rsquo;re not just covering shifts — we&rsquo;re protecting trust, one branch at a time.
            </p>
          </div>
        </div>
      </section>

      {/* CTA — navy */}
      <section className="bg-[#0B132B] text-white py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">Join us.</h2>
          <p className="mt-4 text-lg text-slate-400">14-day free trial. No card required.</p>
          <Link
            to="/register?role=manager"
            className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors"
            data-testid="about-cta"
          >
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
